// File containing functions to append or delete nodes into expandedRows
import { GridApi, IRowNode, RowNode } from 'ag-grid-community';
import { ExpandedRowT } from 'business/definitions';

class ParentNotFound extends Error {}

const getParentExpandedRow = (
    expandedRows: ExpandedRowT[],
    node: IRowNode,
): ExpandedRowT => {
    // We return a root ExpandedRowT object with expandedRows as children
    if (node.level === 0 || node.parent === null)
        return { value: '', children: expandedRows };

    const grandParentExpandedRow = getParentExpandedRow(
        expandedRows,
        node.parent,
    );

    const parentExpandedRow = grandParentExpandedRow.children.find(
        (expandedRow) => expandedRow.value === node.parent?.key,
    );
    if (parentExpandedRow === undefined)
        throw new ParentNotFound('Parent not found');

    return parentExpandedRow;
};

export const addNodeToExpandedRows = (
    expandedRows: ExpandedRowT[],
    node: IRowNode,
): ExpandedRowT[] => {
    const newExpandedRow: ExpandedRowT = { value: node.key, children: [] };
    if (node.level === 0) {
        if (expandedRows.find((row) => row.value === node.key) === undefined)
            expandedRows.push(newExpandedRow);

        return expandedRows;
    }

    try {
        const parentChildren = getParentExpandedRow(expandedRows, node);
        parentChildren.children.push(newExpandedRow);
    } catch (e) {
        if (e instanceof ParentNotFound)
            console.error('Expanded row parent not found');
        else throw e;
    }

    return expandedRows;
};

export const removeNodeFromExpandedRows = (
    expandedRows: ExpandedRowT[],
    node: IRowNode,
): ExpandedRowT[] => {
    if (node.level === 0) {
        return expandedRows.filter((child) => child.value !== node.key);
    }

    try {
        const parentExpandedRow = getParentExpandedRow(expandedRows, node);
        parentExpandedRow.children = parentExpandedRow.children.filter(
            (child) => child.value !== node.key,
        );
    } catch (e) {
        if (e instanceof ParentNotFound)
            console.error('Expanded row parent not found');
        else throw e;
    }
    return expandedRows;
};

/** @private */
export const getExpandedTree = (node: IRowNode): ExpandedRowT[] => {
    const expandedChildren: ExpandedRowT[] = [];

    if (node.childrenAfterGroup) {
        node.childrenAfterGroup.forEach((childNode) => {
            if (!childNode.expanded) return;

            const currentExpandedRow = {
                value: childNode.key,
                children: getExpandedTree(childNode),
            };
            expandedChildren.push(currentExpandedRow);
        });
    }

    return expandedChildren;
};

export const getActualExpandedTree = (
    gridApi: GridApi | null,
): ExpandedRowT[] => {
    const tree: ExpandedRowT[] = [];
    if (!gridApi) return tree;

    gridApi.forEachNode((node: RowNode) => {
        if (node.level !== 0) return;
        if (!node.expanded) return;

        const currentLevelZeroNodeExpandedRow: ExpandedRowT = {
            value: node.key,
            children: getExpandedTree(node),
        };

        tree.push(currentLevelZeroNodeExpandedRow);
    });
    return tree;
};
